import React, { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import SearchIcon from '@mui/icons-material/Search'
import RouterIcon from '@mui/icons-material/Router'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import { useAppTranslation } from 'src/admin/core/hooks'
import { AgencyData, ScreenStatus } from 'src/admin/core/services/screens/screenDashboardService'
import { useScreenDashboardContext } from '../ScreenDashboardContext'

interface ConsortiumHierarchyProps {
  agencies: AgencyData[]
  agenciesCount: number
  serversCount: number
}

const STATUS_CONFIG: Record<ScreenStatus, { label: string; color: string; bg: string }> = {
  online: { label: 'ONLINE', color: '#22c55e', bg: '#dcfce7' },
  offline: { label: 'OFFLINE', color: '#ef4444', bg: '#fee2e2' },
  crash_loop: { label: 'CRASH LOOP', color: '#f97316', bg: '#ffedd5' },
  disconnected: { label: 'DISCONNECTED', color: '#6b7280', bg: '#f3f4f6' },
}

const StatusBadge = ({ status }: { status: ScreenStatus }) => {
  const cfg = STATUS_CONFIG[status]
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.5,
        py: 0.3,
        borderRadius: 10,
        bgcolor: cfg.bg,
        border: `1px solid ${cfg.color}`,
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: cfg.color,
        }}
      />
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, fontSize: '0.65rem', color: cfg.color, textTransform: 'uppercase' }}
      >
        {cfg.label}
      </Typography>
    </Box>
  )
}

const ConsortiumHierarchy = ({ agencies, agenciesCount, serversCount }: ConsortiumHierarchyProps) => {
  const { label } = useAppTranslation()
  const { selectedScreenGroupId, setSelectedScreenGroupId, dashboardData } = useScreenDashboardContext()
  const [expandedAgencies, setExpandedAgencies] = useState<Set<string>>(new Set(['JS002']))
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const getEventLabel = (type: string) => {
    if (!type) return '-'
    const key = `EVENT_${type}_LABEL`
    const val = label(key as any)
    return val === key ? type : val
  }

  const getEventTooltip = (type: string) => {
    if (!type) return label('SIN_EVENTO' as any)
    const key = `EVENT_${type}_TOOLTIP`
    const val = label(key as any)
    return val === key ? `${label('EVENT_DEFAULT_TOOLTIP' as any)} ${type}` : val
  }

  const toggleAgency = (id: string) => {
    setExpandedAgencies((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const filteredAgencies = agencies
    .map((agency) => {
      const searchLower = search.toLowerCase()
      const matchAgencyName = agency.agency_name.toLowerCase().includes(searchLower)

      const filteredScreens = agency.screens.filter((screen) => {
        const matchStatus = statusFilter === 'all' || screen.status === statusFilter
        const matchScreenGroup =
          !selectedScreenGroupId ||
          (screen.screen_config_id === selectedScreenGroupId && screen.status !== 'online')
        const matchScreenSearch =
          !search ||
          matchAgencyName ||
          screen.display.toLowerCase().includes(searchLower) ||
          screen.hostname.toLowerCase().includes(searchLower)

        return matchStatus && matchScreenGroup && matchScreenSearch
      })

      return { ...agency, screens: filteredScreens }
    })
    .filter((agency) => agency.screens.length > 0)
    .sort((a, b) => a.agency_name.localeCompare(b.agency_name, undefined, { numeric: true, sensitivity: 'base' }))

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {/* Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <RouterIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
            {label('CONSORTIUM_HIERARCHY' as any)}
          </Typography>
        </Box>

        {/* Filters — left aligned immediately after title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={label('SEARCH_AGENCIES_PLACEHOLDER' as any)}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 200, '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ fontSize: '0.8rem' }}>
              <MenuItem value="all">{label('ALL_STATUSES' as any)}</MenuItem>
              <MenuItem value="online">{label('ONLINE' as any)}</MenuItem>
              <MenuItem value="offline">{label('OFFLINE' as any)}</MenuItem>
              <MenuItem value="crash_loop">{label('CRASH_LOOP' as any)}</MenuItem>
              <MenuItem value="disconnected">{label('DISCONNECTED' as any)}</MenuItem>
            </Select>
          </FormControl>
          {(() => {
            const selectedGroup = dashboardData?.screen_groups.find((g) => g.screen_id === selectedScreenGroupId)
            if (!selectedGroup) return null
            return (
              <Chip
                label={`Screen: ${selectedGroup.screen_name}`}
                onDelete={() => setSelectedScreenGroupId(null)}
                color="primary"
                variant="outlined"
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  borderColor: 'primary.main',
                  bgcolor: 'action.selected',
                }}
              />
            )
          })()}
        </Box>

        {/* Counters — pushed to the right */}
        <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
          <Typography
            variant="caption"
            sx={{ textTransform: 'uppercase', color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem' }}
          >
            AGENCIAS: {agenciesCount}
          </Typography>
          <Typography
            variant="caption"
            sx={{ textTransform: 'uppercase', color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem' }}
          >
            SERVIDORES: {serversCount}
          </Typography>
        </Box>
      </Box>

      {/* Agency Rows */}
      {filteredAgencies.map((agency) => {
        const isExpanded = expandedAgencies.has(agency.agency_id)
        return (
          <Box key={agency.agency_id} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
            {/* Agency Header Row */}
            <Box
              onClick={() => toggleAgency(agency.agency_id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2.5,
                py: 1.5,
                cursor: 'pointer',
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'action.hover' },
                transition: 'background-color 0.15s ease',
                gap: 1.5,
              }}
            >
              <IconButton size="small" sx={{ p: 0.25 }}>
                {isExpanded ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />}
              </IconButton>
              <RouterIcon sx={{ fontSize: 18, color: agency.has_db_error ? '#ef4444' : 'text.secondary' }} />
              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                {agency.agency_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({agency.screens.length} Displays)
              </Typography>

              {/* Badges */}
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                {agency.has_db_error && (
                  <Chip
                    icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />}
                    label="DB ERROR"
                    size="small"
                    sx={{
                      bgcolor: '#ef4444',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      height: 24,
                      '& .MuiChip-icon': { color: '#fff' },
                    }}
                  />
                )}
                {agency.screens.some((s) => s.status === 'offline') && (
                  <Tooltip title="Una o más pantallas se encuentran Offline" arrow placement="top">
                    <Chip
                      icon={<ReportProblemIcon sx={{ fontSize: '14px !important' }} />}
                      label="Alerta"
                      size="small"
                      sx={{
                        bgcolor: '#f97316',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        height: 24,
                        '& .MuiChip-icon': { color: '#fff' },
                      }}
                    />
                  </Tooltip>
                )}
                {agency.is_critical && (
                  <Chip
                    icon={<ReportProblemIcon sx={{ fontSize: '14px !important' }} />}
                    label="CRITICAL"
                    size="small"
                    sx={{
                      bgcolor: '#ef4444', // changed to red to distinct from orange
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      height: 24,
                      '& .MuiChip-icon': { color: '#fff' },
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Screens Table */}
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ px: 4, pb: 1, bgcolor: 'action.hover' }}>
                <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.78rem', py: 0.8 } }}>
                  <TableHead>
                    <TableRow>
                      {['DISPLAY', 'HOSTNAME', 'ESTADO', 'EVENTO', 'LAST_HEARTBEAT', 'UPTIME', 'RESTARTS'].map((col) => (
                        <TableCell
                          key={col}
                          sx={{
                            textTransform: 'uppercase',
                            letterSpacing: 0.8,
                            fontWeight: 700,
                            fontSize: '0.62rem',
                            color: 'text.disabled',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            py: 1,
                          }}
                        >
                          {col === 'ESTADO' ? label('ESTADO' as any) : col === 'EVENTO' ? 'EVENTO' : label(col as any)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const groupedByServer = agency.screens.reduce((acc, screen) => {
                        const serverId = screen.server_id || 'unknown'
                        if (!acc[serverId]) acc[serverId] = []
                        acc[serverId].push(screen)
                        return acc
                      }, {} as Record<string, typeof agency.screens>)

                      return Object.entries(groupedByServer).map(([serverId, screens]) => {
                        const serverName = screens[0]?.server_name
                        const showServerHeader = serverName && serverName !== '-' && serverName !== 'unknown'
                        return (
                          <React.Fragment key={serverId}>
                            {showServerHeader && (
                              <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell
                                  colSpan={7}
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    color: 'text.primary',
                                    py: 1,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  SERVIDOR: {serverName}
                                </TableCell>
                              </TableRow>
                            )}
                            {screens.map((screen) => (
                              <TableRow key={screen.screen_id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                <TableCell sx={{ color: 'text.secondary' }}>
                                  {screen.screen_name && screen.screen_name !== '-'
                                    ? `${screen.display} - ${screen.screen_name}`
                                    : screen.display}
                                </TableCell>
                                <TableCell sx={{ color: 'text.secondary' }}>{screen.hostname}</TableCell>
                                <TableCell>
                                  <StatusBadge status={screen.status} />
                                </TableCell>
                                <TableCell>
                                  <Tooltip title={getEventTooltip(screen.event_type_raw)} arrow placement="top">
                                    <Typography variant="body2" sx={{ fontSize: '0.78rem', cursor: 'help' }}>
                                      {getEventLabel(screen.event_type_raw)}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  <Tooltip title={screen.last_heartbeat_full} arrow placement="top">
                                    <Typography variant="body2" sx={{ fontSize: '0.78rem', cursor: 'help' }}>
                                      {screen.last_heartbeat}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>{screen.uptime}</TableCell>
                                <TableCell>{screen.restarts}</TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        )
                      })
                    })()}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </Box>
        )
      })}
    </Paper>
  )
}

export default ConsortiumHierarchy
